import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import SapCurrencyDropdown from '../../../../components/common/SapCurrencyDropdown';
import toastService from '../../../../utils/toastService';

const OtherLaborPriceForm = ({
    visible,
    onHide,
    onSubmit,
    initialData = null,
    isLoading = false
}) => {
    const { th } = useLanguage();
    const { hasDealerRole, hasServiceDealerRole, hasSalesDealerRole } = usePermissions();
    
    // Dealer kontrolü
    const isDealer = hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole();
    
    const [submitted, setSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        price: null,
        currency: null,
        contractStartDate: null,
        contractEndDate: null,
        isActive: 1
    });

    useEffect(() => {
        if (visible) {
            if (initialData) {
                // Handle both PascalCase and camelCase from backend
                const isActive = initialData.IsActive !== undefined ? initialData.IsActive : 
                                (initialData.isActive !== undefined ? initialData.isActive : 1);
                setFormData({
                    price: initialData.price || initialData.Price || null,
                    currency: initialData.currency || initialData.Currency || null,
                    contractStartDate: initialData.contractStartDate || initialData.ContractStartDate
                        ? new Date(initialData.contractStartDate || initialData.ContractStartDate)
                        : null,
                    contractEndDate: initialData.contractEndDate || initialData.ContractEndDate
                        ? new Date(initialData.contractEndDate || initialData.ContractEndDate)
                        : null,
                    isActive: isActive
                });
            } else {
                setFormData({
                    price: null,
                    currency: null,
                    contractStartDate: null,
                    contractEndDate: null,
                    isActive: 1
                });
            }
            setSubmitted(false);
        }
    }, [visible, initialData]);

    const handleChange = (field, value) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };
            // If start date changes and end date exists and is before new start date, clear end date
            if (field === 'contractStartDate' && updated.contractEndDate && value && updated.contractEndDate < value) {
                updated.contractEndDate = null;
            }
            return updated;
        });
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.price || formData.price <= 0) {
            errors.price = th('otherLaborPrice.validation.priceRequired');
        }

        if (!formData.currency) {
            errors.currency = th('otherLaborPrice.validation.currencyRequired');
        }

        if (!formData.contractStartDate) {
            errors.contractStartDate =
                th('otherLaborPrice.validation.contractStartDateRequired');
        }

        if (formData.contractEndDate && formData.contractStartDate && formData.contractEndDate < formData.contractStartDate) {
            errors.contractEndDate =
                th('otherLaborPrice.validation.contractEndDateAfterStart');
        }

        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        setSubmitted(true);

        if (!validateForm()) {
            toastService.error(
                th('otherLaborPrice.validation.formInvalid')
            );
            return;
        }

        const payload = {
            ...formData,
            currency: formData.currency
        };

        if (initialData?.id) {
            payload.id = initialData.id;
        }

        try {
            await onSubmit(payload);
            onHide();
        } catch (error) {
            console.error('Form submit error:', error);
        }
    };

    const renderFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button
                    label={th('common.cancel') || 'Vazgeç'}
                    icon="pi pi-times"
                    onClick={onHide}
                    className="p-button-secondary"
                    disabled={isLoading}
                />
                <Button
                    label={th('common.save') || 'Kaydet'}
                    icon="pi pi-check"
                    onClick={handleSubmit}
                    loading={isLoading}
                />
            </div>
        );
    };

    return (
        <Dialog
            header={
                initialData
                    ? th('otherLaborPrice.form.editTitle')
                    : th('otherLaborPrice.form.createTitle')
            }
            visible={visible}
            style={{ width: '50vw' }}
            footer={renderFooter()}
            onHide={onHide}
            modal
            className="p-fluid"
        >
            <div className="grid">
                <div className="col-12">
                    <label htmlFor="price" className="block text-900 font-medium mb-2">
                        {th('otherLaborPrice.form.price')} <span className="text-red-500">*</span>
                    </label>
                    <InputNumber
                        id="price"
                        value={formData.price}
                        onValueChange={(e) => handleChange('price', e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={8}
                        className={`w-full ${submitted && (!formData.price || formData.price <= 0) ? 'p-invalid' : ''}`}
                        disabled={isLoading}
                    />
                    {submitted && (!formData.price || formData.price <= 0) && (
                        <small className="p-error block mt-1">
                            {th('otherLaborPrice.validation.priceRequired')}
                        </small>
                    )}
                </div>

                <div className="col-12">
                    <label htmlFor="currency" className="block text-900 font-medium mb-2">
                        {th('otherLaborPrice.form.currency') || 'Para Birimi'} <span className="text-red-500">*</span>
                    </label>
                    <SapCurrencyDropdown
                        value={formData.currency}
                        onChange={(e) => handleChange('currency', e.value)}
                        className={`w-full ${submitted && !formData.currency ? 'p-invalid' : ''}`}
                        disabled={isLoading}
                        isDealer={isDealer}
                    />
                    {submitted && !formData.currency && (
                        <small className="p-error block mt-1">
                            {th('otherLaborPrice.validation.currencyRequired')}
                        </small>
                    )}
                </div>

                <div className="col-12 md:col-6">
                    <label htmlFor="contractStartDate" className="block text-900 font-medium mb-2">
                        {th('otherLaborPrice.form.contractStartDate')}{' '}
                        <span className="text-red-500">*</span>
                    </label>
                    <Calendar
                        id="contractStartDate"
                        value={formData.contractStartDate}
                        onChange={(e) => handleChange('contractStartDate', e.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        placeholder={th('otherLaborPrice.form.contractStartDatePlaceholder')}
                        className={`w-full ${submitted && !formData.contractStartDate ? 'p-invalid' : ''}`}
                        disabled={isLoading}
                    />
                    {submitted && !formData.contractStartDate && (
                        <small className="p-error block mt-1">
                            {th('otherLaborPrice.validation.contractStartDateRequired')}
                        </small>
                    )}
                </div>

                <div className="col-12 md:col-6">
                    <label htmlFor="contractEndDate" className="block text-900 font-medium mb-2">
                        {th('otherLaborPrice.form.contractEndDate')}
                    </label>
                    <Calendar
                        id="contractEndDate"
                        value={formData.contractEndDate}
                        onChange={(e) => handleChange('contractEndDate', e.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        placeholder={th('otherLaborPrice.form.contractEndDatePlaceholder')}
                        minDate={formData.contractStartDate || null}
                        className="w-full"
                        disabled={isLoading}
                    />
                    {submitted &&
                        formData.contractEndDate &&
                        formData.contractStartDate &&
                        formData.contractEndDate < formData.contractStartDate && (
                            <small className="p-error block mt-1">
                                {th('otherLaborPrice.validation.contractEndDateAfterStart')}
                            </small>
                        )}
                </div>

                {initialData && (
                    <div className="col-12">
                        <div className="flex align-items-center">
                            <Checkbox
                                inputId="isActive"
                                checked={formData.isActive === 1}
                                onChange={(e) => handleChange('isActive', e.checked ? 1 : 0)}
                                disabled={isLoading}
                            />
                            <label htmlFor="isActive" className="ml-2">
                                {th('common.active') || 'Aktif'}
                            </label>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default OtherLaborPriceForm;

